/**
 * useSupportChat — SCK-549 widget-side chat hook.
 *
 * Owns the SSE handshake+stream contract locked in PR-2:
 *
 *   1. POST {API_URL}/v1/support-bot/chat/session   (Bearer <caller JWT>)
 *        body: { prompt: "<latest user message>", entryPoint: "widget" }
 *        response: { token: "<opaque, single-use, short-TTL>" }
 *
 *   2. GET  {API_URL}/v1/support-bot/chat/stream?token=<opaque>
 *        SSE stream — EventSource cannot carry Authorization, so the
 *        handshake trades the caller JWT for a short-lived opaque
 *        token (coord PR-2 enforces TTL + one-time use + caller-scope).
 *
 * The hook NEVER elevates: it uses whatever token getToken() returns.
 * It NEVER calls Anthropic/OpenAI directly — the SSE proxy in coord
 * forwards to the Daari LLM Gateway per memory/daari_llm_gateway.md.
 *
 * Streaming events:
 *   event: chunk       data: {text}       → append to assistant msg
 *   event: tool_call   data: {name}       → surface as inline chip
 *   event: ticket      data: {id, sla}    → surface as ticket card
 *   event: done                            → mark stream complete
 *   event: stream_error data: {message}   → surface + close
 *
 * PR-7 scope: hook is scaffolded against the contract. Server side
 * lands in PR-2 (coord) + PR-8 (dedicated /support page). The widget
 * degrades gracefully when the backend is absent (handshake POST
 * errors → user sees a soft error message, never a crash).
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { API_URL } from "../../helpers/constants";
import { getToken } from "../../helpers/tokenStorage";
import {
  COPY,
  ROLE_ASSISTANT,
  ROLE_USER,
  SSE_EVENT_CHUNK,
  SSE_EVENT_DONE,
  SSE_EVENT_ERROR,
  SSE_EVENT_TICKET,
  SSE_EVENT_TOOL,
  SUPPORT_CHAT_HANDSHAKE_PATH,
  SUPPORT_CHAT_STREAM_PATH,
} from "./constants";

const genId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export default function useSupportChat() {
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const esRef = useRef(null);

  // Tear down any open EventSource on unmount — nothing worse than an
  // orphaned SSE reconnecting into a route the user already left.
  useEffect(() => {
    return () => {
      if (esRef.current) {
        try {
          esRef.current.close();
        } catch {
          /* best-effort cleanup */
        }
        esRef.current = null;
      }
    };
  }, []);

  const appendChunkToLast = useCallback((assistantId, text) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === assistantId
          ? { ...m, content: (m.content || "") + text }
          : m
      )
    );
  }, []);

  const attachEventToLast = useCallback((assistantId, event) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === assistantId
          ? { ...m, events: [...(m.events || []), event] }
          : m
      )
    );
  }, []);

  const finalizeLast = useCallback((assistantId, patch) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === assistantId ? { ...m, streaming: false, ...patch } : m
      )
    );
    setIsStreaming(false);
  }, []);

  /**
   * openStream — connect EventSource to the coord SSE proxy with the
   * opaque token, then wire the four locked event names to state.
   */
  const openStream = useCallback(
    (streamToken, assistantId) => {
      // API_URL already ends in "/api"; do NOT prepend.
      const url = `${API_URL}${SUPPORT_CHAT_STREAM_PATH}?token=${encodeURIComponent(
        streamToken
      )}`;

      const es = new EventSource(url);
      esRef.current = es;

      es.addEventListener(SSE_EVENT_CHUNK, (evt) => {
        try {
          const { text } = JSON.parse(evt.data || "{}");
          if (text) appendChunkToLast(assistantId, text);
        } catch {
          /* malformed frame — ignore, do NOT log evt.data (PII) */
        }
      });

      es.addEventListener(SSE_EVENT_TOOL, (evt) => {
        try {
          const { name } = JSON.parse(evt.data || "{}");
          if (name)
            attachEventToLast(assistantId, { kind: "tool", name });
        } catch {
          /* ignore */
        }
      });

      es.addEventListener(SSE_EVENT_TICKET, (evt) => {
        try {
          const { id, sla } = JSON.parse(evt.data || "{}");
          if (id)
            attachEventToLast(assistantId, {
              kind: "ticket",
              id,
              sla: sla || null,
            });
        } catch {
          /* ignore */
        }
      });

      es.addEventListener(SSE_EVENT_ERROR, (evt) => {
        let message = COPY.streamError;
        try {
          const parsed = JSON.parse(evt.data || "{}");
          if (parsed?.message && typeof parsed.message === "string") {
            message = parsed.message;
          }
        } catch {
          /* keep default */
        }
        finalizeLast(assistantId, { error: message });
        setError(message);
        es.close();
        esRef.current = null;
      });

      es.addEventListener(SSE_EVENT_DONE, () => {
        finalizeLast(assistantId, {});
        es.close();
        esRef.current = null;
      });

      // Transport-level failure (network dropped, coord rebooted). We
      // do NOT log the URL or token; only the fact.
      es.onerror = () => {
        finalizeLast(assistantId, { error: COPY.streamError });
        setError(COPY.streamError);
        es.close();
        esRef.current = null;
      };
    },
    [appendChunkToLast, attachEventToLast, finalizeLast]
  );

  /**
   * sendMessage — public API. Appends the user turn, POSTs the
   * handshake, and opens the SSE stream on the returned token. All
   * error paths degrade to a red inline note in the assistant bubble
   * (no toast, no console.error carrying user text).
   */
  const sendMessage = useCallback(
    async (rawText) => {
      const text = (rawText || "").trim();
      if (!text || isStreaming) return;

      setError(null);

      const userMsg = {
        id: genId(),
        role: ROLE_USER,
        content: text,
        ts: Date.now(),
      };
      const assistantMsg = {
        id: genId(),
        role: ROLE_ASSISTANT,
        content: "",
        streaming: true,
        events: [],
        ts: Date.now(),
      };

      const nextMessages = [...messages, userMsg];
      setMessages([...nextMessages, assistantMsg]);
      setIsStreaming(true);

      const jwt = getToken();
      if (!jwt) {
        // Not signed in — degrade cleanly. The widget shouldn't render
        // outside the auth shell, so this branch is defensive.
        finalizeLast(assistantMsg.id, {
          error: "Sign in to use support chat.",
        });
        return;
      }

      try {
        const res = await fetch(`${API_URL}${SUPPORT_CHAT_HANDSHAKE_PATH}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            prompt: text,
            entryPoint: "widget",
          }),
        });

        if (!res.ok) {
          // Do NOT log body — it may echo user prompt for validation.
          finalizeLast(assistantMsg.id, {
            error: `Support service returned ${res.status}.`,
          });
          setError(`Support service returned ${res.status}.`);
          return;
        }

        const data = await res.json().catch(() => null);
        const streamToken = data?.token;
        if (!streamToken || typeof streamToken !== "string") {
          finalizeLast(assistantMsg.id, {
            error: "Support handshake returned no stream token.",
          });
          return;
        }

        openStream(streamToken, assistantMsg.id);
      } catch (e) {
        // Network failure — never surface stack traces to the console
        // (some frameworks include the request body in error text).
        finalizeLast(assistantMsg.id, { error: COPY.streamError });
        setError(COPY.streamError);
      }
    },
    [messages, isStreaming, openStream, finalizeLast]
  );

  const reset = useCallback(() => {
    if (esRef.current) {
      try {
        esRef.current.close();
      } catch {
        /* ignore */
      }
      esRef.current = null;
    }
    setMessages([]);
    setIsStreaming(false);
    setError(null);
  }, []);

  const cancelStreaming = useCallback(() => {
    if (esRef.current) {
      try {
        esRef.current.close();
      } catch {
        /* ignore */
      }
      esRef.current = null;
    }
    // Mark any in-flight assistant msg as no longer streaming so the
    // shimmer stops and the user can send again.
    setMessages((prev) =>
      prev.map((m) => (m.streaming ? { ...m, streaming: false } : m))
    );
    setIsStreaming(false);
  }, []);

  return {
    messages,
    isStreaming,
    error,
    sendMessage,
    reset,
    cancelStreaming,
  };
}
